import { NavLink } from 'react-router-dom';

const tabs = [
  { label: 'Dashboard', to: '/admin/email' },
  { label: 'Mail Logs', to: '/admin/email/logs' },
  { label: 'Campaigns', to: '/admin/email/campaigns' },
  { label: 'Templates', to: '/admin/email/templates' },
  { label: 'Audience', to: '/admin/email/audience' },
  { label: 'Queue', to: '/admin/email/queue' },
  { label: 'Settings', to: '/admin/email/settings' },
  { label: 'Reports', to: '/admin/email/reports' },
];

const badgeStyles = {
  sent: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  processing: 'bg-sky-50 text-sky-700 ring-sky-100',
  queued: 'bg-amber-50 text-amber-700 ring-amber-100',
  scheduled: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  draft: 'bg-slate-100 text-slate-700 ring-slate-200',
  paused: 'bg-orange-50 text-orange-700 ring-orange-100',
  failed: 'bg-rose-50 text-rose-700 ring-rose-100',
  rejected: 'bg-rose-50 text-rose-700 ring-rose-100',
  skipped: 'bg-slate-100 text-slate-700 ring-slate-200',
  cancelled: 'bg-slate-100 text-slate-700 ring-slate-200',
};

export const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

export const formatShortDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
};

export const downloadCsv = (text, filename) => {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const StatusBadge = ({ value }) => {
  const key = String(value || 'draft').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.draft;
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{String(value || 'draft').replace(/_/g, ' ')}</span>;
};

export const StatCard = ({ label, value, hint, tone = 'slate' }) => {
  const toneMap = {
    emerald: 'border-emerald-100 bg-emerald-50/60 text-emerald-700',
    rose: 'border-rose-100 bg-rose-50/70 text-rose-700',
    amber: 'border-amber-100 bg-amber-50/70 text-amber-700',
    sky: 'border-sky-100 bg-sky-50/70 text-sky-700',
    slate: 'border-slate-200 bg-white text-slate-900',
  };
  const cls = toneMap[tone] || toneMap.slate;
  return (
    <div className={`rounded-[24px] border p-4 shadow-sm ${cls}`}>
      <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500">{label}</p>
      <div className="mt-4 text-3xl font-semibold">{value}</div>
      {hint ? <p className="mt-2 text-sm text-slate-500">{hint}</p> : null}
    </div>
  );
};

export const PageSection = ({ title, subtitle, action, children }) => (
  <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-700">{title}</p>
        {subtitle ? <p className="mt-2 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
    {children}
  </section>
);

export const EmptyState = ({ title, body }) => (
  <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50/60 px-6 py-10 text-center">
    <p className="text-lg font-semibold text-slate-900">{title}</p>
    <p className="mt-2 text-sm text-slate-500">{body}</p>
  </div>
);

export const TextField = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <input
      {...props}
      className={`w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 ${props.className || ''}`}
    />
  </label>
);

export const SelectField = ({ label, children, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <select
      {...props}
      className={`w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 ${props.className || ''}`}
    >
      {children}
    </select>
  </label>
);

export const TextAreaField = ({ label, ...props }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <textarea
      {...props}
      className={`min-h-[140px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 ${props.className || ''}`}
    />
  </label>
);

const EmailCenterHeader = ({ title, description, actions = null }) => (
  <div className="rounded-[30px] border border-slate-200 bg-white px-6 py-6 shadow-sm">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700">Email Center</p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">{title}</h1>
        <p className="mt-3 max-w-3xl text-sm text-slate-500">{description}</p>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
    </div>
    <div className="mt-6 flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.to === '/admin/email'}
          className={({ isActive }) =>
            [
              'rounded-full border px-4 py-2 text-sm font-semibold transition',
              isActive
                ? 'border-emerald-600 bg-emerald-600 text-white shadow-[0_10px_24px_rgba(15,118,110,0.22)]'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900',
            ].join(' ')
          }
        >
          {tab.label}
        </NavLink>
      ))}
    </div>
  </div>
);

export default function EmailCenterPage({ title, description, actions, children }) {
  return (
    <div className="space-y-6">
      <EmailCenterHeader title={title} description={description} actions={actions} />
      {children}
    </div>
  );
}
